/**
 * Clipboard copy for the `share` block.
 *
 * Delegation-based: listens on document so any `[data-share-copy]` button
 * rendered anywhere (including content loaded dynamically) hooks in without
 * per-instance wiring. Copies `data-share-url` when present, falling back
 * to the current page URL.
 *
 * For triggers with `.share__copy__default` + `.share__copy__success`
 * children (the main copy button), swaps their visibility for 2s after a
 * successful copy. For simpler triggers (the platform icon buttons that
 * fall through to copy — Instagram/Discord), toggles an `.is-copied`
 * class that CSS can hook for a brief confirmation tint.
 */
export const initShare = () => {
    const SUCCESS_MS = 2000;

    const showDefaultAgain = (trigger) => {
        const def = trigger.querySelector('.share__copy__default');
        const ok  = trigger.querySelector('.share__copy__success');
        if (def && ok) {
            def.hidden = false;
            ok.hidden = true;
        }
        trigger.classList.remove('is-copied');
    };

    const showSuccess = (trigger) => {
        const def = trigger.querySelector('.share__copy__default');
        const ok  = trigger.querySelector('.share__copy__success');
        if (def && ok) {
            def.hidden = true;
            ok.hidden = false;
        }
        trigger.classList.add('is-copied');
        clearTimeout(trigger._copyTimer);
        trigger._copyTimer = setTimeout(() => showDefaultAgain(trigger), SUCCESS_MS);
    };

    document.addEventListener('click', async (event) => {
        const trigger = event.target.closest('[data-share-copy]');
        if (!trigger) return;

        event.preventDefault();

        const url = trigger.dataset.shareUrl || window.location.href;

        try {
            await navigator.clipboard.writeText(url);
            showSuccess(trigger);
        } catch (err) {
            // Insecure context (non-HTTPS) or old browser. Fall back to the
            // legacy document.execCommand approach via a temporary input.
            const temp = document.createElement('input');
            temp.value = url;
            temp.setAttribute('readonly', '');
            temp.style.position = 'absolute';
            temp.style.left = '-9999px';
            document.body.appendChild(temp);
            temp.select();
            try {
                document.execCommand('copy');
                showSuccess(trigger);
            } catch {
                console.warn('Clipboard copy failed', err);
            }
            document.body.removeChild(temp);
        }
    });
};
